/**
 * The uploads of one place on a page — the ONE way the web shows a file
 * uploading (decided 2026-10-18).
 *
 * Give it the files the user picked and it lists them AT ONCE, where they will
 * live, each with its progress (`items`): the screen renders them as a
 * `FileAttachmentRow` or a `PhotoThumb` with `upload={item.progress}`, so the
 * ring sits on the file itself instead of in a panel. They upload in
 * parallel, a network drop is retried by `run` (`lib/upload/retry.ts`), a
 * failure stays on screen with «Reintentar», and the screen is kept on while
 * anything uploads (a locked phone freezes the page and kills the upload).
 *
 * When one lands it leaves `items` and `onDone` hands the result to the screen,
 * which adds it to its own list — so a finished file is just a file again.
 *
 * Only for uploads that start when the file is PICKED. A file that uploads
 * when the user presses Save shows nothing of its own: the Save button's
 * spinner already says it (owner's decision, 2026-10-18).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createWakeLock, type WakeLockController, type WakeLockEnv } from '../lib/wakeLock';
import { isUploadRefusal, type UploadProgress, type UploadState } from '../lib/upload/retry';

export interface UploadItem {
    /** Stable key for the row while it uploads. */
    readonly id: string;
    readonly file: File;
    readonly progress: UploadProgress;
    /** A local `blob:` preview (when `preview` said so): show it in the
     *  thumbnail while the bytes go. Yours to keep after `onDone`; revoked
     *  here when the upload is cancelled. */
    readonly previewUrl?: string;
}

export type UploadRunner<R> = (
    file: File,
    options: { onState: (state: UploadState) => void; signal: AbortSignal },
) => Promise<R>;

export interface UseUploadsOptions<R> {
    onDone: (item: UploadItem, result: R) => void;
    /** Told about a failure (to toast a refusal's reason, say). A cut
     *  connection's row stays as «No se pudo subir» with «Reintentar»; a
     *  refusal's row shows `errorMessage` and no retry (`isUploadRefusal`). */
    onError?: (item: UploadItem, error: unknown) => void;
    /** Make a local preview for these files (images, for a thumbnail). */
    preview?: (file: File) => boolean;
    /** What a failed row says: its tooltip, and the row's own text when the
     *  server refused the file. Pass a TRANSLATED reason: without it the row
     *  falls back to the error's raw `message`. */
    errorMessage?: (error: unknown, item: UploadItem) => string;
}

export interface UseUploads {
    items: UploadItem[];
    /** True while at least one file is still uploading (not counting failed ones). */
    busy: boolean;
    /** Lists the files and starts them. Resolves when all of them settled. */
    start: (files: File[]) => Promise<void>;
    retry: (id: string) => void;
    /** Cancels an upload in progress, or drops a failed one. */
    cancel: (id: string) => void;
}

function isAbort(error: unknown): boolean {
    return error instanceof Error && error.name === 'AbortError';
}

let seq = 0;

export function useUploads<R>(run: UploadRunner<R>, options: UseUploadsOptions<R>): UseUploads {
    const [items, setItems] = useState<UploadItem[]>([]);
    const runRef = useRef(run);
    runRef.current = run;
    const optionsRef = useRef(options);
    optionsRef.current = options;
    const controllers = useRef(new Map<string, AbortController>());
    const itemsRef = useRef<UploadItem[]>([]);
    itemsRef.current = items;
    const mounted = useRef(true);

    // Built in the effect, not once: StrictMode disposes and re-runs it.
    const [wakeLock, setWakeLock] = useState<WakeLockController | null>(null);
    useEffect(() => {
        mounted.current = true;
        const lock = createWakeLock({
            wakeLock: typeof navigator === 'undefined'
                ? null
                : (navigator as unknown as { wakeLock?: WakeLockEnv['wakeLock'] }).wakeLock ?? null,
            document: typeof document === 'undefined' ? null : document,
        });
        setWakeLock(lock);
        const live = controllers.current;
        return () => {
            mounted.current = false;
            lock.dispose();
            for (const c of live.values()) c.abort();
            live.clear();
            for (const item of itemsRef.current) if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
        };
    }, []);

    const busy = items.some((i) => i.progress.kind !== 'failed');
    useEffect(() => {
        if (!busy || !wakeLock) return undefined;
        return wakeLock.hold();
    }, [busy, wakeLock]);

    const patch = useCallback((id: string, progress: UploadProgress) => {
        if (!mounted.current) return;
        setItems((prev) => prev.map((i) => (i.id === id ? { ...i, progress } : i)));
    }, []);

    const remove = useCallback((id: string, revoke: boolean) => {
        const item = itemsRef.current.find((i) => i.id === id);
        if (revoke && item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
        controllers.current.delete(id);
        if (!mounted.current) return;
        setItems((prev) => prev.filter((i) => i.id !== id));
    }, []);

    const launch = useCallback(async (item: UploadItem): Promise<void> => {
        const controller = new AbortController();
        controllers.current.set(item.id, controller);
        patch(item.id, { kind: 'uploading', loaded: 0, total: item.file.size });
        try {
            const result = await runRef.current(item.file, {
                onState: (state) => {
                    if (!controller.signal.aborted) patch(item.id, state);
                },
                signal: controller.signal,
            });
            if (controller.signal.aborted) return;
            remove(item.id, false);
            optionsRef.current.onDone(item, result);
        } catch (error) {
            if (controller.signal.aborted || isAbort(error)) return;
            controllers.current.delete(item.id);
            const message = optionsRef.current.errorMessage?.(error, item)
                ?? (error instanceof Error ? error.message : '');
            patch(item.id, { kind: 'failed', message, retryable: !isUploadRefusal(error) });
            optionsRef.current.onError?.(item, error);
        }
    }, [patch, remove]);

    const start = useCallback(async (files: File[]) => {
        if (files.length === 0) return;
        const fresh: UploadItem[] = files.map((file) => ({
            id: `up-${Date.now().toString(36)}-${(seq += 1)}`,
            file,
            progress: { kind: 'uploading', loaded: 0, total: file.size },
            previewUrl: optionsRef.current.preview?.(file) ? URL.createObjectURL(file) : undefined,
        }));
        setItems((prev) => [...prev, ...fresh]);
        itemsRef.current = [...itemsRef.current, ...fresh];
        await Promise.all(fresh.map((item) => launch(item)));
    }, [launch]);

    const retry = useCallback((id: string) => {
        const item = itemsRef.current.find((i) => i.id === id);
        if (item && item.progress.kind === 'failed' && item.progress.retryable !== false) void launch(item);
    }, [launch]);

    const cancel = useCallback((id: string) => {
        controllers.current.get(id)?.abort();
        remove(id, true);
    }, [remove]);

    return { items, busy, start, retry, cancel };
}
